import { useEffect, useRef, useState } from 'react';
import { DICE_CATALOG, DICE_SKINS, type DiceSkin } from '@shared/game/types';
import { Icon } from './Icon';

const PIPS: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

const FACE_ROTATIONS: Record<number, { x: number; y: number }> = {
  1: { x: 0, y: 0 },
  2: { x: -90, y: 0 },
  3: { x: 0, y: -90 },
  4: { x: 0, y: 90 },
  5: { x: 90, y: 0 },
  6: { x: 0, y: 180 },
};

const FACE_CLASS: Record<number, string> = {
  1: 'front',
  2: 'top',
  3: 'right',
  4: 'left',
  5: 'bottom',
  6: 'back',
};

function DieFace({ value }: { value: number }) {
  return (
    <div className={`die-face die-face--${FACE_CLASS[value]}`}>
      <div className="die-face__grid">
        {Array.from({ length: 9 }, (_, index) => (
          <span key={index} className={`die-face__slot ${PIPS[value]?.includes(index) ? 'is-on' : ''}`}>
            <span className="die-face__pip" />
          </span>
        ))}
      </div>
    </div>
  );
}

interface DiceProps {
  value: number | null;
  skin: DiceSkin;
  rollKey?: string;
  animations?: boolean;
  size?: 'normal' | 'small';
  effect?: 'astral';
}

export function Dice({ value, skin, rollKey, animations = true, size = 'normal', effect }: DiceProps) {
  const [rotation, setRotation] = useState({ x: -8, y: 12 });
  const [rolling, setRolling] = useState(false);
  const spinRef = useRef(0);
  const previousKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    const face = FACE_ROTATIONS[value ?? 1] ?? FACE_ROTATIONS[1]!;
    if (!animations || !rollKey || previousKey.current === rollKey) {
      previousKey.current = rollKey;
      setRotation({ x: face.x - 8, y: face.y + 12 });
      setRolling(false);
      return undefined;
    }
    previousKey.current = rollKey;
    spinRef.current += 1;
    setRolling(true);
    setRotation({
      x: face.x - 8 + spinRef.current * 720,
      y: face.y + 12 + spinRef.current * 1080,
    });
    const timeout = window.setTimeout(() => setRolling(false), 950);
    return () => window.clearTimeout(timeout);
  }, [value, rollKey, animations]);

  return (
    <div
      className={`die die--${skin} ${rolling ? 'die--rolling' : ''} ${size === 'small' ? 'die--small' : ''} ${effect === 'astral' ? 'die--astral-effect' : ''}`}
      aria-label={value ? `Dado: ${value}` : 'Dado sin tirar'}
      role="img"
    >
      <span className="die__aura" aria-hidden="true" />
      <div className="die__scene">
        <div
          className="die__cube"
          style={{
            transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
            transitionDuration: animations ? '900ms' : '0ms',
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((face) => (
            <DieFace key={face} value={face} />
          ))}
        </div>
      </div>
      <span className="die__contact" aria-hidden="true" />
    </div>
  );
}

interface DicePickerProps {
  value: DiceSkin;
  onChange: (skin: DiceSkin) => void;
  unlocked?: DiceSkin[];
  compact?: boolean;
}

export function DicePicker({ value, onChange, unlocked = [...DICE_SKINS], compact = false }: DicePickerProps) {
  return (
    <div className={`dice-picker ${compact ? 'dice-picker--compact' : ''}`}>
      {DICE_CATALOG.map((dice) => {
        const locked = Boolean(dice.premium) && !unlocked.includes(dice.id);
        return (
          <button
            key={dice.id}
            type="button"
            className={`dice-choice ${value === dice.id ? 'is-selected' : ''} ${locked ? 'is-locked' : ''}`}
            onClick={() => {
              if (!locked) onChange(dice.id);
            }}
            disabled={locked}
            title={locked ? 'Disponible en el Pack Cosmético' : dice.description}
          >
            <Dice value={5} skin={dice.id} animations={false} size="small" />
            <span className="dice-choice__label">
              {dice.name}
              {locked && <Icon name="lock" size={12} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}
